import React, { useState, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImg from './cropUtils'; // Ensure cropUtils.js exists in same folder

const TestimonialsTab = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [formData, setFormData] = useState({ name: '', designation: '', message: '' });
    const [image, setImage] = useState(null); // This will hold the final file/blob to upload
    const [loading, setLoading] = useState(false);
    
    // Edit State
    const [editingId, setEditingId] = useState(null);
    
    // Cropper State
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropping, setIsCropping] = useState(false);

    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const fetchTestimonials = async () => {
        try {
            const res = await fetch('/api/testimonials');
            const data = await res.json();
            setTestimonials(data);
        } catch (error) {
            console.error("Error fetching testimonials:", error);
        }
    };

    const handleEditClick = (t) => {
        setEditingId(t.id);
        setFormData({
            name: t.name,
            designation: t.designation,
            message: t.message
        });
        setImage(null);
        setImageSrc(null);
        setIsCropping(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({ name: '', designation: '', message: '' });
        setImage(null);
        setImageSrc(null);
        setIsCropping(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // --- CROPPER LOGIC START ---
    const onFileChange = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result);
                setIsCropping(true);
            });
            reader.readAsDataURL(file);
        }
    };

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const showCroppedImage = async () => {
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            setImage(croppedBlob); // Set the blob as the image to be uploaded
            setIsCropping(false);
        } catch (e) {
            console.error(e);
        }
    };
    // --- CROPPER LOGIC END ---

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const data = new FormData();
        data.append('name', formData.name);
        data.append('designation', formData.designation);
        data.append('message', formData.message);
        
        if (image) {
            // Append the blob with a filename
            data.append('image', image, 'testimonial.webp');
        }

        const url = editingId ? `/api/testimonials/${editingId}` : '/api/testimonials';
        const method = editingId ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method: method,
                body: data
            });

            if (res.ok) {
                fetchTestimonials();
                handleCancelEdit();
            } else {
                alert("Failed to save testimonial.");
            }
        } catch (error) {
            console.error("Error saving testimonial:", error);
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this testimonial?")) return;
        try {
            await fetch(`/api/testimonials/${id}`, { method: 'DELETE' });
            fetchTestimonials();
            if (editingId === id) handleCancelEdit();
        } catch (error) {
            console.error("Error deleting:", error);
        }
    };

    return (
        <div className="space-y-8 relative">

            {/* --- CROPPER MODAL (Overlay) --- */}
            {isCropping && imageSrc && (
                <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
                    <div className="relative w-full max-w-lg h-96 bg-slate-800 rounded-lg overflow-hidden border border-slate-600">
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1} // Square aspect ratio
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </div>
                    <div className="w-full max-w-lg mt-4 flex items-center gap-4">
                        <span className="text-white text-xs font-bold">Zoom</span>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e) => setZoom(e.target.value)}
                            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <div className="mt-6 flex gap-4">
                        <button onClick={() => { setIsCropping(false); setImageSrc(null); if(fileInputRef.current) fileInputRef.current.value=''; }} className="px-6 py-2 bg-slate-600 text-white rounded-full font-bold uppercase text-xs hover:bg-slate-500">
                            Cancel
                        </button>
                        <button onClick={showCroppedImage} className="px-8 py-2 bg-brand-orange text-white rounded-full font-bold uppercase text-xs hover:bg-orange-600">
                            Crop & Save
                        </button>
                    </div>
                </div>
            )}

            {/* ADD / EDIT FORM */}
            <div className={`bg-white p-6 rounded-2xl shadow-sm border border-slate-100 transition-all duration-300 ${isCropping ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-lg font-black uppercase ${editingId ? 'text-blue-600' : 'text-slate-800'}`}>
                        {editingId ? 'Edit Testimonial' : 'Add Testimonial'}
                    </h3>
                    {editingId && (
                        <button onClick={handleCancelEdit} className="text-xs text-red-500 hover:underline font-bold">
                            Cancel Edit
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase">Name</label>
                        <input 
                            required 
                            value={formData.name} 
                            onChange={e => setFormData({...formData, name: e.target.value})} 
                            className="w-full p-2 border rounded font-bold focus:outline-none focus:border-blue-500 transition-colors" 
                            placeholder="e.g. Rahul Sharma" 
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase">Designation / School</label>
                        <input 
                            required 
                            value={formData.designation} 
                            onChange={e => setFormData({...formData, designation: e.target.value})} 
                            className="w-full p-2 border rounded focus:outline-none focus:border-blue-500 transition-colors" 
                            placeholder="e.g. Principal, DPS" 
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs font-bold text-slate-400 uppercase">Message</label>
                        <textarea 
                            required 
                            value={formData.message} 
                            onChange={e => setFormData({...formData, message: e.target.value})} 
                            className="w-full p-2 border rounded h-24 focus:outline-none focus:border-blue-500 transition-colors" 
                            placeholder="Write the review here..." 
                        />
                    </div>
                    
                    {/* UPDATED PHOTO INPUT SECTION */}
                    <div className="col-span-2 md:col-span-1">
                        <label className="text-xs font-bold text-slate-400 uppercase block mb-2">
                            {editingId ? 'Change Photo (Optional)' : 'Photo (Optional)'}
                        </label>
                        
                        <div className="flex items-center gap-4">
                            {/* Preview Circle */}
                            <div className="w-16 h-16 bg-slate-100 rounded-full overflow-hidden border-2 border-slate-200 flex-shrink-0">
                                {image ? (
                                    <img src={URL.createObjectURL(image)} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400 text-center leading-tight">No<br/>Img</div>
                                )}
                            </div>
                            
                            {/* File Input */}
                            <input 
                                ref={fileInputRef}
                                type="file" 
                                accept="image/*" 
                                onChange={onFileChange} 
                                className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" 
                            />
                        </div>
                    </div>

                    <div className="flex items-end col-span-2 md:col-span-1">
                        <button 
                            type="submit" 
                            disabled={loading} 
                            className={`w-full text-white px-6 py-2.5 rounded-lg font-bold uppercase text-xs shadow-lg transition-all transform active:scale-95 ${
                                editingId 
                                ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200' 
                                : 'bg-orange-500 hover:bg-orange-600 shadow-orange-200'
                            }`}
                        >
                            {loading ? 'Processing...' : (editingId ? 'Update Testimonial' : 'Add Testimonial')}
                        </button>
                    </div>
                </form>
            </div>

            {/* LIST */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((t) => (
                    <div 
                        key={t.id} 
                        className={`bg-white p-6 rounded-2xl shadow-sm border relative group transition-all duration-300 ${editingId === t.id ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50' : 'border-slate-100 hover:shadow-md'}`}
                    >
                        {/* Action Buttons */}
                        <div className="absolute top-4 right-4 flex gap-2">
                            <button onClick={() => handleEditClick(t)} className="text-slate-300 hover:text-blue-500 transition-colors p-1" title="Edit">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                </svg>
                            </button>
                            <button onClick={() => handleDelete(t.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1" title="Delete">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                            </button>
                        </div>

                        <div className="flex items-center gap-4 mb-4">
                            {t.image_path ? (
                                <img src={`${t.image_path}?t=${Date.now()}`} alt={t.name} className="w-14 h-14 rounded-full object-cover shadow-sm border-2 border-white" />
                            ) : (
                                <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 font-bold text-xl">
                                    {t.name.charAt(0)}
                                </div>
                            )}
                            <div>
                                <h4 className="font-bold text-slate-900 text-lg">{t.name}</h4>
                                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide">{t.designation}</p>
                            </div>
                        </div>
                        <p className="text-sm text-slate-600 italic leading-relaxed">"{t.message}"</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TestimonialsTab;